/**
 * Time shift validation: break window must lie within shift start/end (overnight-safe).
 */

export type TimeShiftValidationInput = {
  startTime?: unknown;
  endTime?: unknown;
  breakStartTime?: unknown;
  breakEndTime?: unknown;
};

const TIME_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

/** Parse HH:mm or HH:mm:ss to minutes from midnight (0–1439). */
export function parseTimeToMinutes(value: unknown): number | null {
  if (value === undefined || value === null) return null;
  const text = String(value).trim();
  if (!text) return null;
  const match = text.match(TIME_RE);
  if (!match) return null;
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

const MINUTES_PER_DAY = 24 * 60;

function hasTimeValue(value: unknown): boolean {
  return value !== undefined && value !== null && String(value).trim() !== "";
}

/** Shift interval [start, end] on extended timeline (end may exceed 1440 for overnight). */
function shiftIntervalMinutes(
  startMin: number,
  endMin: number
): { start: number; end: number } {
  let end = endMin;
  if (end <= startMin) {
    end += MINUTES_PER_DAY;
  }
  return { start: startMin, end };
}

/** Place break interval on same extended timeline as shift. */
function breakIntervalOnShiftTimeline(
  shiftStart: number,
  shiftEnd: number,
  breakStart: number,
  breakEnd: number
): { start: number; end: number } | null {
  const shift = shiftIntervalMinutes(shiftStart, shiftEnd);
  let bs = breakStart;
  let be = breakEnd;
  if (be <= bs) {
    be += MINUTES_PER_DAY;
  }
  if (shift.end > MINUTES_PER_DAY && bs < shift.start) {
    bs += MINUTES_PER_DAY;
    if (be <= bs) {
      be += MINUTES_PER_DAY;
    } else if (be < shift.start) {
      be += MINUTES_PER_DAY;
    }
  }
  if (be <= bs) {
    return null;
  }
  return { start: bs, end: be };
}

/**
 * Returns field-keyed errors (bind names: start_time, end_time, break_start_time, break_end_time).
 */
export function validateTimeShiftTimes(
  input: TimeShiftValidationInput
): Record<string, string> {
  const errors: Record<string, string> = {};

  const hasBreakStart = hasTimeValue(input.breakStartTime);
  const hasBreakEnd = hasTimeValue(input.breakEndTime);

  if (hasBreakStart !== hasBreakEnd) {
    const msg =
      "Break Start Time and Break End Time must both be set or both left empty.";
    if (!hasBreakStart) {
      errors.break_start_time = msg;
    }
    if (!hasBreakEnd) {
      errors.break_end_time = msg;
    }
    return errors;
  }

  const start = parseTimeToMinutes(input.startTime);
  const end = parseTimeToMinutes(input.endTime);

  if (start === null) {
    errors.start_time = "Start Time is required and must be in HH:MM format.";
    return errors;
  }

  if (end === null) {
    errors.end_time = "End Time is required and must be in HH:MM format.";
    return errors;
  }

  if (start === end) {
    errors.end_time = "End Time must be different from Start Time.";
    return errors;
  }

  if (!hasBreakStart) {
    return errors;
  }

  const breakStart = parseTimeToMinutes(input.breakStartTime);
  const breakEnd = parseTimeToMinutes(input.breakEndTime);

  if (breakStart === null) {
    errors.break_start_time = "Break Start Time must be in HH:MM format.";
    return errors;
  }
  if (breakEnd === null) {
    errors.break_end_time = "Break End Time must be in HH:MM format.";
    return errors;
  }

  if (breakStart === breakEnd) {
    errors.break_end_time = "Break End Time must be after Break Start Time.";
    return errors;
  }

  const rawBreak = breakIntervalOnShiftTimeline(start, end, breakStart, breakEnd);
  if (!rawBreak) {
    errors.break_end_time = "Break End Time must be after Break Start Time.";
    return errors;
  }

  const shift = shiftIntervalMinutes(start, end);
  const withinShift =
    rawBreak.start >= shift.start && rawBreak.end <= shift.end;

  if (!withinShift) {
    const msg =
      "Break times must fall within shift Start Time and End Time.";
    if (rawBreak.start < shift.start) {
      errors.break_start_time = msg;
    }
    if (rawBreak.end > shift.end) {
      errors.break_end_time = msg;
    }
    if (!errors.break_start_time && !errors.break_end_time) {
      errors.break_start_time = msg;
    }
  }

  return errors;
}

/** First user-facing message from validateTimeShiftTimes. */
export function timeShiftValidationSummary(
  input: TimeShiftValidationInput
): string | null {
  const errors = validateTimeShiftTimes(input);
  const keys = Object.keys(errors);
  if (keys.length === 0) return null;
  return errors[keys[0]];
}
